import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { ChevronRight } from 'lucide-react';
import { getAllTypes } from '../data/typesData';
import { useLanguage } from '../contexts/LanguageContext';
import StarChart from '../components/viz/StarChart';
import DisplayHeading from '../components/primitives/DisplayHeading';
import HairlineDivider from '../components/primitives/HairlineDivider';
import MonoCaption from '../components/primitives/MonoCaption';

/* =====================================================
 * Layout
 * ===================================================== */

const Page = styled.div`
  position: relative;
  min-height: 100vh;
  background: ${p => p.theme.colors.bgPrimary};
  color: ${p => p.theme.colors.text};
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(ellipse 60% 40% at 30% 20%, rgba(156,123,201,0.06), transparent 60%),
      radial-gradient(ellipse 50% 35% at 75% 80%, rgba(107,140,203,0.05), transparent 65%);
    pointer-events: none;
    z-index: 0;
  }
`;

const Container = styled.div`
  position: relative;
  z-index: 1;
  max-width: ${p => p.theme.containers.default};
  margin: 0 auto;
  padding: 80px 24px;

  @media (max-width: ${p => p.theme.breakpoints.mobile}) {
    padding: 56px 16px;
  }
`;

const HeadingWrap = styled.div`
  text-align: center;
  margin-bottom: 32px;
`;

/* =====================================================
 * Filter chips (軸別グルーピング)
 * ===================================================== */

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  margin: 32px 0 36px;
`;

const FilterChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px;
  background: ${p => p.$active ? p.theme.colors.bgTertiary : 'transparent'};
  border: 1px solid ${p => p.$active ? p.theme.colors.lineGold : p.theme.colors.lineFaint};
  border-radius: ${p => p.theme.borderRadius.full};
  color: ${p => p.$active ? p.theme.colors.starGold : p.theme.colors.textMuted};
  font-family: ${p => p.theme.fonts.body};
  font-size: 0.8rem;
  letter-spacing: 0.04em;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: ${p => p.theme.colors.text};
    border-color: ${p => p.theme.colors.lineSoft};
  }
`;

const ChipDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${p => p.$color};
  flex-shrink: 0;
  box-shadow: 0 0 8px ${p => p.$color}66;
`;

const CountCaption = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

/* =====================================================
 * Card grid
 * ===================================================== */

const TypesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;

  @media (max-width: ${p => p.theme.breakpoints.desktop}) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: ${p => p.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const TypeCard = styled(motion.div)`
  position: relative;
  display: grid;
  grid-template-columns: 78px 1fr auto;
  align-items: center;
  gap: 14px;
  padding: 18px 18px 18px 16px;
  background: ${p => p.theme.colors.gradCard};
  border: 1px solid ${p => p.theme.colors.lineFaint};
  border-top: 2px solid ${p => p.$accentColor};
  border-radius: ${p => p.theme.borderRadius.md};
  cursor: pointer;
  transition: transform 0.25s ease, border-color 0.25s ease, background 0.25s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: ${p => p.theme.colors.lineSoft};
    border-top-color: ${p => p.$accentColor};
    background: ${p => p.theme.colors.bgHover};
  }
`;

const StarWrap = styled.div`
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TypeInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const TypeCode = styled.span`
  font-family: ${p => p.theme.fonts.mono};
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.2em;
  color: ${p => p.theme.colors.starGold};
`;

const TypeName = styled.h3`
  margin: 0;
  font-family: ${p => p.theme.fonts.display};
  font-style: italic;
  font-weight: 500;
  font-size: 1.15rem;
  color: ${p => p.theme.colors.text};
  line-height: 1.2;
`;

const TypeArche = styled.span`
  font-family: ${p => p.theme.fonts.body};
  font-size: 0.78rem;
  color: ${p => p.theme.colors.textMuted};
  letter-spacing: 0.02em;
  margin-top: 2px;
`;

const TypeTag = styled.p`
  margin: 6px 0 0;
  font-size: 0.78rem;
  line-height: 1.55;
  color: ${p => p.theme.colors.textSecondary};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Chevron = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${p => p.theme.colors.textMuted};
  transition: color 0.2s, transform 0.2s;

  ${TypeCard}:hover & {
    color: ${p => p.theme.colors.starGold};
    transform: translateX(2px);
  }
`;

const EmptyHint = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  padding: 48px 16px;
  color: ${p => p.theme.colors.textMuted};
  font-size: 0.9rem;
`;

/* =====================================================
 * Axis groupings (code prefix → axis key)
 * ===================================================== */

const AXIS_FROM_PREFIX = {
  AC: 'activation',
  JD: 'judgment',
  CH: 'choice',
  RS: 'resonance',
  AW: 'awareness',
};
const INTEGRATION_CODES = new Set(['BLNC', 'CMPL', 'ADPT']);

const AXIS_COLORS = {
  activation: '#F4B860',
  judgment:   '#6B8CCB',
  choice:     '#9C7BC9',
  resonance:  '#D08A8A',
  awareness:  '#7FB89A',
  integration:'#E6C77E',
};

const getGroupOf = (code) => {
  if (INTEGRATION_CODES.has(code)) return 'integration';
  return AXIS_FROM_PREFIX[code.slice(0, 2)] || 'integration';
};

const FILTERS = [
  { key: 'all',         label_ja: 'すべて',  label_en: 'All',          color: '#E6C77E' },
  { key: 'activation',  label_ja: '起動',    label_en: 'Activation',   color: AXIS_COLORS.activation },
  { key: 'judgment',    label_ja: '判断',    label_en: 'Judgment',     color: AXIS_COLORS.judgment },
  { key: 'choice',      label_ja: '選択',    label_en: 'Choice',       color: AXIS_COLORS.choice },
  { key: 'resonance',   label_ja: '共鳴',    label_en: 'Resonance',    color: AXIS_COLORS.resonance },
  { key: 'awareness',   label_ja: '自覚',    label_en: 'Awareness',    color: AXIS_COLORS.awareness },
  { key: 'integration', label_ja: '統合',    label_en: 'Integration',  color: AXIS_COLORS.integration },
];

/* =====================================================
 * Component
 * ===================================================== */

const TypesListPage = () => {
  const navigate = useNavigate();
  const { lang, t } = useLanguage();

  const types = useMemo(() => getAllTypes(lang), [lang]);
  const [filter, setFilter] = useState('all');

  const visibleTypes = useMemo(() => {
    if (filter === 'all') return types;
    return types.filter(ty => getGroupOf(ty.code) === filter);
  }, [types, filter]);

  return (
    <Page>
      <Container>
        <HeadingWrap>
          <DisplayHeading
            eyebrow={t('24 のかたち', '24 Forms')}
            title={t(
              '24 のかたちを、ひとつずつ眺めてみる。',
              'A quiet walk through twenty-four forms.'
            )}
            subtitle={t(
              '5 つの軸の重なり方から、24 のかたちが立ちあがります。それぞれに、固有の星座と短い言葉を添えました。',
              'From how five axes overlap arise twenty-four forms. Each carries its own constellation and a short line.'
            )}
            align="center"
            italic
          />
        </HeadingWrap>

        <FilterRow>
          {FILTERS.map(f => (
            <FilterChip
              key={f.key}
              $active={filter === f.key}
              onClick={() => setFilter(f.key)}
            >
              {f.key !== 'all' && <ChipDot $color={f.color} />}
              {lang === 'en' ? f.label_en : f.label_ja}
            </FilterChip>
          ))}
        </FilterRow>

        <CountCaption>
          <MonoCaption upper>
            {filter === 'all'
              ? t(`24 のかたち`, `24 forms`)
              : t(`${visibleTypes.length} のかたち`, `${visibleTypes.length} forms`)}
          </MonoCaption>
        </CountCaption>

        <TypesGrid>
          {visibleTypes.length === 0 ? (
            <EmptyHint>
              {t('該当するかたちが見つかりませんでした。', 'No forms match this filter.')}
            </EmptyHint>
          ) : (
            visibleTypes.map((type, index) => {
              const group = getGroupOf(type.code);
              const accent = AXIS_COLORS[group];
              return (
                <TypeCard
                  key={type.code}
                  $accentColor={accent}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: Math.min(index * 0.025, 0.4) }}
                  onClick={() => navigate(`/types/${type.code}`)}
                >
                  <StarWrap>
                    <StarChart code={type.code} size={64} color={accent} />
                  </StarWrap>
                  <TypeInfo>
                    <TypeCode>{type.code}</TypeCode>
                    <TypeName>{type.name}</TypeName>
                    {type.archetype && <TypeArche>{type.archetype}</TypeArche>}
                    {type.tagline && <TypeTag>{type.tagline}</TypeTag>}
                  </TypeInfo>
                  <Chevron>
                    <ChevronRight size={18} strokeWidth={1.6} />
                  </Chevron>
                </TypeCard>
              );
            })
          )}
        </TypesGrid>

        <HairlineDivider mark="✦" margin="56px 0 0" />
      </Container>
    </Page>
  );
};

export default TypesListPage;
